import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, MessageSquare, Layers, BookOpen } from 'lucide-react';

const DetailedPatternAnalysis = ({ patternAnalysis, annotations, essayText }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Analyze sensory details from API-provided data
  const analyzeSensoryDetails = () => {
    // Use API-provided sensory instances if available
    if (patternAnalysis?.sensoryImagery?.instances && 
        Array.isArray(patternAnalysis.sensoryImagery.instances)) {
      
      // Group instances by type
      const grouped = {
        visual: [],
        auditory: [],
        tactile: [],
        olfactory: [],
        gustatory: []
      };
      
      patternAnalysis.sensoryImagery.instances.forEach(instance => {
        if (grouped[instance.type]) {
          grouped[instance.type].push({
            word: instance.text.split(' ').slice(0, 3).join(' '), // First few words for display
            context: instance.context || instance.text,
            fullText: instance.text
          });
        }
      });
      
      return {
        found: grouped,
        score: patternAnalysis.sensoryImagery.score || 0,
        totalFound: patternAnalysis.sensoryImagery.instances.length,
        summary: patternAnalysis.sensoryImagery.summary,
        fromAPI: true
      };
    }
    
    // No API data available - don't do naive word matching
    return {
      found: {
        visual: [],
        auditory: [],
        tactile: [],
        olfactory: [],
        gustatory: []
      },
      score: 0,
      totalFound: 0,
      pending: true,
      message: "Sensory analysis will be available after AI review"
    };
  };

  // Get context around a word
  const getWordContext = (text, word) => {
    const index = text.toLowerCase().indexOf(word.toLowerCase());
    if (index === -1) return null;
    
    const start = Math.max(0, index - 30);
    const end = Math.min(text.length, index + word.length + 30);
    const context = text.slice(start, end);
    
    return context.length > word.length + 10 ? context : null;
  };

  // Analyze sentence variety
  const analyzeSentenceVariety = () => {
    if (!essayText) return { score: 5, issues: [], suggestions: [] };

    const sentences = essayText.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const lengths = sentences.map(s => s.trim().split(/\s+/).length);
    
    const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
    const shortSentences = lengths.filter(l => l < 8).length;
    const longSentences = lengths.filter(l => l > 20).length;
    const mediumSentences = lengths.filter(l => l >= 8 && l <= 20).length;
    
    const variety = Math.abs(shortSentences - longSentences) <= 2 ? 2 : 0;
    const balance = mediumSentences > sentences.length * 0.4 ? 2 : 1;
    const lengthVariety = Math.max(...lengths) - Math.min(...lengths) > 10 ? 2 : 1;
    
    let score = patternAnalysis?.sentenceVariety?.score || Math.min(10, 4 + variety + balance + lengthVariety);
    
    const issues = [];
    const suggestions = [];
    
    if (shortSentences > sentences.length * 0.7) {
      issues.push(`${shortSentences}/${sentences.length} sentences are quite short (under 8 words)`);
      suggestions.push('Combine some short sentences or add descriptive details');
    }
    
    if (longSentences > sentences.length * 0.4) {
      issues.push(`${longSentences}/${sentences.length} sentences are very long (over 20 words)`);
      suggestions.push('Break up some long sentences for better readability');
    }
    
    if (Math.abs(avgLength - 15) > 5) {
      issues.push(`Average sentence length is ${avgLength.toFixed(1)} words (ideal: 12-18)`);
      suggestions.push('Aim for a mix of sentence lengths averaging 12-18 words');
    }

    return { score, issues, suggestions, stats: { short: shortSentences, medium: mediumSentences, long: longSentences, avg: avgLength.toFixed(1) } };
  };

  // Analyze content depth
  const analyzeContentDepth = () => {
    const contentIssues = annotations.filter(a => a.category === 'content' && a.severity === 'major');
    const score = Math.max(1, 10 - (contentIssues.length * 3));
    
    const suggestions = [];
    const strengths = [];
    
    if (contentIssues.length === 0) {
      strengths.push('Strong narrative depth throughout the essay');
    }
    
    if (contentIssues.some(a => a.feedback?.toLowerCase().includes('specific'))) {
      suggestions.push('Add more specific details and examples to support your points');
    }
    
    if (contentIssues.some(a => a.feedback?.toLowerCase().includes('develop'))) {
      suggestions.push('Further develop key moments with deeper reflection');
    }
    
    if (contentIssues.some(a => a.feedback?.toLowerCase().includes('show'))) {
      suggestions.push('Show more through actions and scenes rather than telling');
    }

    return { score, issues: contentIssues, suggestions, strengths };
  };

  // Analyze structure
  const analyzeStructure = () => {
    const structureIssues = annotations.filter(a => a.category === 'structure');
    const score = Math.max(1, 10 - (structureIssues.length * 2));
    
    const suggestions = [];
    
    if (structureIssues.some(a => a.feedback?.toLowerCase().includes('transition'))) {
      suggestions.push('Strengthen transitions between paragraphs and ideas');
    }
    
    if (structureIssues.some(a => a.feedback?.toLowerCase().includes('flow'))) {
      suggestions.push('Improve logical flow of ideas throughout the essay');
    }
    
    if (structureIssues.some(a => a.feedback?.toLowerCase().includes('organization'))) {
      suggestions.push('Reorganize content for better narrative structure');
    }

    return { score, issues: structureIssues, suggestions };
  };

  const sensoryAnalysis = analyzeSensoryDetails();
  const varietyAnalysis = analyzeSentenceVariety();
  const depthAnalysis = analyzeContentDepth();
  const structureAnalysis = analyzeStructure();

  const sections = [
    {
      key: 'sensory',
      title: 'Sensory Details',
      icon: Eye,
      score: sensoryAnalysis.score,
      data: sensoryAnalysis,
      pending: sensoryAnalysis.pending
    },
    {
      key: 'variety',
      title: 'Sentence Variety',
      icon: MessageSquare,
      score: varietyAnalysis.score,
      data: varietyAnalysis
    },
    {
      key: 'depth',
      title: 'Content Depth',
      icon: Layers,
      score: depthAnalysis.score,
      data: depthAnalysis
    },
    {
      key: 'structure',
      title: 'Structure & Flow',
      icon: BookOpen,
      score: structureAnalysis.score,
      data: structureAnalysis
    }
  ];

  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="bg-stone-50 px-4 py-3 border-b border-stone-200">
        <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
          <BookOpen className="w-4 h-4" />
          Writing Patterns Analysis
        </h3>
      </div>
      
      <div className="divide-y divide-stone-200">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.has(section.key);
          const scoreColor = section.score >= 8 ? 'text-emerald-600' : section.score >= 6 ? 'text-yellow-600' : 'text-red-600';
          
          return (
            <div key={section.key} className="bg-white">
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-stone-600" />
                  <span className="text-sm font-medium text-stone-800">{section.title}</span>
                  {section.pending ? (
                    <span className="text-sm text-stone-400 italic">Pending</span>
                  ) : (
                    <span className={`text-lg font-bold ${scoreColor}`}>
                      {section.score}/10
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-stone-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-stone-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 bg-stone-50">
                  {section.key === 'sensory' && (
                    <SensoryDetailSection data={section.data} />
                  )}
                  {section.key === 'variety' && (
                    <SentenceVarietySection data={section.data} />
                  )}
                  {section.key === 'depth' && (
                    <ContentDepthSection data={section.data} />
                  )}
                  {section.key === 'structure' && (
                    <StructureSection data={section.data} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Component for sensory details section
const SensoryDetailSection = ({ data }) => (
  <div className="space-y-3 text-sm">
    <div>
      <h4 className="font-semibold text-stone-700 mb-2">
        {data.pending ? (
          <>Sensory Elements: <span className="text-stone-400 font-normal">Analysis pending</span></>
        ) : (
          <>Sensory Elements Found ({data.totalFound}):</>
        )}
      </h4>
      {data.pending ? (
        <div className="p-3 bg-stone-50 rounded border border-stone-200 text-center text-stone-500 italic">
          {data.message || "Sensory detail analysis will be available after AI review"}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.found).map(([sense, items]) => (
            <div key={sense} className="p-2 bg-white rounded border">
              <div className="font-medium text-stone-600 capitalize mb-1">{sense} ({items.length})</div>
              {items.length > 0 ? (
                <div className="space-y-1">
                  {items.slice(0, 3).map((item, i) => (
                    <div key={i} className="text-xs text-stone-500">
                      <span className="font-medium">"{item.fullText || item.word}"</span>
                      {item.context && <div className="italic mt-0.5">...{item.context}...</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-stone-400 italic">None found</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    
    {!data.pending && data.score <= 8 && (
      <div className="p-3 bg-azure-50 rounded border border-azure-200">
        <h5 className="font-semibold text-azure-800 mb-1">Improvement Opportunities:</h5>
        <ul className="text-azure-700 space-y-1 text-xs">
          <li>• Add what you saw: colors, lighting, visual details of the setting</li>
          <li>• Include what you heard: conversations, sounds, background noise</li>
          <li>• Describe what you felt: textures, temperatures, physical sensations</li>
          <li>• Mention smells or tastes that were memorable in the moment</li>
        </ul>
      </div>
    )}
  </div>
);

// Component for sentence variety section
const SentenceVarietySection = ({ data }) => (
  <div className="space-y-3 text-sm">
    <div className="grid grid-cols-4 gap-2">
      <div className="p-2 bg-white rounded border text-center">
        <div className="text-lg font-bold text-stone-700">{data.stats.short}</div>
        <div className="text-xs text-stone-500">Short (≤8 words)</div>
      </div>
      <div className="p-2 bg-white rounded border text-center">
        <div className="text-lg font-bold text-stone-700">{data.stats.medium}</div>
        <div className="text-xs text-stone-500">Medium (9-20)</div>
      </div>
      <div className="p-2 bg-white rounded border text-center">
        <div className="text-lg font-bold text-stone-700">{data.stats.long}</div>
        <div className="text-xs text-stone-500">Long (20+ words)</div>
      </div>
      <div className="p-2 bg-white rounded border text-center">
        <div className="text-lg font-bold text-stone-700">{data.stats.avg}</div>
        <div className="text-xs text-stone-500">Avg Length</div>
      </div>
    </div>
    
    {data.issues.length > 0 && (
      <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
        <h5 className="font-semibold text-yellow-800 mb-1">Issues Identified:</h5>
        <ul className="text-yellow-700 space-y-1 text-xs">
          {data.issues.map((issue, i) => (
            <li key={i}>• {issue}</li>
          ))}
        </ul>
      </div>
    )}
    
    {data.suggestions.length > 0 && (
      <div className="p-3 bg-azure-50 rounded border border-azure-200">
        <h5 className="font-semibold text-azure-800 mb-1">Suggestions for Improvement:</h5>
        <ul className="text-azure-700 space-y-1 text-xs">
          {data.suggestions.map((suggestion, i) => (
            <li key={i}>• {suggestion}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Component for content depth section
const ContentDepthSection = ({ data }) => (
  <div className="space-y-3 text-sm">
    {data.strengths.length > 0 && (
      <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
        <h5 className="font-semibold text-emerald-800 mb-1">Strengths:</h5>
        <ul className="text-emerald-700 space-y-1 text-xs">
          {data.strengths.map((strength, i) => (
            <li key={i}>• {strength}</li>
          ))}
        </ul>
      </div>
    )}
    
    {data.issues.length > 0 && (
      <div className="p-3 bg-red-50 rounded border border-red-200">
        <h5 className="font-semibold text-red-800 mb-1">Areas Needing Development:</h5>
        <div className="space-y-2">
          {data.issues.map((issue, i) => (
            <div key={i} className="text-red-700 text-xs">
              <div className="font-medium">"{issue.originalText}"</div>
              <div className="italic mt-0.5">{issue.feedback}</div>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {data.suggestions.length > 0 && (
      <div className="p-3 bg-azure-50 rounded border border-azure-200">
        <h5 className="font-semibold text-azure-800 mb-1">Improvement Strategies:</h5>
        <ul className="text-azure-700 space-y-1 text-xs">
          {data.suggestions.map((suggestion, i) => (
            <li key={i}>• {suggestion}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Component for structure section
const StructureSection = ({ data }) => (
  <div className="space-y-3 text-sm">
    {data.issues.length > 0 && (
      <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
        <h5 className="font-semibold text-yellow-800 mb-1">Structural Issues:</h5>
        <div className="space-y-2">
          {data.issues.map((issue, i) => (
            <div key={i} className="text-yellow-700 text-xs">
              <div className="font-medium">"{issue.originalText}"</div>
              <div className="italic mt-0.5">{issue.feedback}</div>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {data.suggestions.length > 0 && (
      <div className="p-3 bg-azure-50 rounded border border-azure-200">
        <h5 className="font-semibold text-azure-800 mb-1">Structural Improvements:</h5>
        <ul className="text-azure-700 space-y-1 text-xs">
          {data.suggestions.map((suggestion, i) => (
            <li key={i}>• {suggestion}</li>
          ))}
        </ul>
      </div>
    )}
    
    {data.score >= 8 && data.issues.length === 0 && (
      <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
        <div className="text-emerald-700 text-xs">
          ✅ Your essay has strong structural flow and organization.
        </div>
      </div>
    )}
  </div>
);

export default DetailedPatternAnalysis;